import { useSyncExternalStore } from "react";

const subscribers = new Set<() => void>();
let pendingRouteModules = 0;

const notify = () => {
  subscribers.forEach((subscriber) => {
    subscriber();
  });
};

/**
 * Runs a lazy route import while publishing whether route code is currently
 * loading. This keeps loading state outside React so route configuration can
 * report pending module work without owning component state.
 */
export async function trackLazyRoute<T>(loadRoute: () => Promise<T>) {
  pendingRouteModules += 1;
  notify();

  try {
    return await loadRoute();
  } finally {
    pendingRouteModules = Math.max(0, pendingRouteModules - 1);
    notify();
  }
}

/**
 * Returns true while one or more lazy route modules are being imported.
 * This intentionally tracks route code loading only, not page-level data
 * fetching performed by the loaded view.
 */
export function useIsLazyRoutePending() {
  return useSyncExternalStore(
    (subscriber) => {
      subscribers.add(subscriber);
      return () => subscribers.delete(subscriber);
    },
    () => pendingRouteModules > 0,
    () => false,
  );
}
